/**
 * useAdminAuth — single hook tying the admin auth client to Jotai state.
 *
 * Mirror of apps/web/src/auth/use-auth.ts, scoped to the Admin bundle.
 * `login` writes the user returned by the cookie-mode endpoint directly;
 * `refreshSession` returns the user on success, null on UNAUTHENTICATED
 * (which clears the atom too). Any other error propagates so callers
 * can surface "couldn't reach api" vs "you're signed out" distinctly.
 */
import { useAtom } from 'jotai'
import { useCallback } from 'react'
import { adminUserAtom } from '../state/atoms.js'
import { authClient, isUnauthenticated } from './auth-client.js'

export function useAdminAuth() {
  const [user, setUser] = useAtom(adminUserAtom)

  const login = useCallback(async (email: string, password: string) => {
    const nextUser = await authClient.login(email, password)
    setUser(nextUser)
    return nextUser
  }, [setUser])

  const refreshSession = useCallback(async () => {
    try {
      const me = await authClient.me()
      setUser(me)
      return me
    } catch (err) {
      if (isUnauthenticated(err)) {
        setUser(null)
        return null
      }
      throw err
    }
  }, [setUser])

  const logout = useCallback(async () => {
    // Logout is idempotent (204 even for an expired session). Until it
    // succeeds, revocation is uncertain: retain identity for a visible retry.
    await authClient.logout()
    setUser(null)
  }, [setUser])

  return { user, login, refreshSession, logout }
}
